import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigation, MapPin, Clock, CheckCircle, Circle, Play, Flag, ArrowRight, RefreshCw, FileText, ChevronDown, ChevronUp, Ship, Anchor, AlertTriangle, Plus, X, Trash2, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = window.location.origin;

const fmtDt = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};
const fmtH = (h) => { if (!h && h !== 0) return '-'; const d = Math.floor(h / 24); const hr = Math.floor(h % 24); const m = Math.round((h % 1) * 60); return d > 0 ? `${d}d ${hr}h${m > 0 ? ` ${m}m` : ''}` : `${hr}h${m > 0 ? ` ${m}m` : ''}`; };

export default function AcompanhamentoViagemPage() {
  const { token, hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'gestor', 'operador']);
  const [acompanhamentos, setAcompanhamentos] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [viagensEmAndamento, setViagensEmAndamento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showIniciar, setShowIniciar] = useState(false);
  const [showRelatorio, setShowRelatorio] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroRota, setFiltroRota] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, rRes, vRes] = await Promise.all([
        fetch(`${API}/api/viagem-acompanhamento`, { headers }),
        fetch(`${API}/api/rotas-checkpoint`, { headers }),
        fetch(`${API}/api/viagens?status=em_andamento`, { headers }),
      ]);
      const aData = await aRes.json();
      setAcompanhamentos(aData);
      setRotas(await rRes.json());
      setViagensEmAndamento(await vRes.json());
      if (aData.length > 0 && !selected) setSelected(aData[0]);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSeed = async () => {
    await fetch(`${API}/api/rotas-checkpoint/seed`, { method: 'POST', headers });
    toast.success('Rotas criadas');
    fetchAll();
  };

  const handleIniciar = async (viagemId, rotaId, vel) => {
    try {
      const r = await fetch(`${API}/api/viagem-acompanhamento/iniciar`, {
        method: 'POST', headers, body: JSON.stringify({ viagem_id: viagemId, rota_id: rotaId, velocidade_media_nos: vel }),
      });
      if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Erro'); return; }
      const data = await r.json();
      toast.success('Acompanhamento iniciado');
      setShowIniciar(false);
      setSelected(data);
      fetchAll();
    } catch { toast.error('Erro'); }
  };

  const handlePassagem = async (cpId, obs) => {
    if (!selected) return;
    const r = await fetch(`${API}/api/viagem-acompanhamento/${selected.id}/registrar-passagem`, {
      method: 'POST', headers, body: JSON.stringify({ checkpoint_id: cpId, observacoes: obs }),
    });
    if (r.ok) {
      toast.success('Passagem registrada');
      const updated = await fetch(`${API}/api/viagem-acompanhamento/${selected.id}`, { headers });
      setSelected(await updated.json());
      fetchAll();
    } else { toast.error('Erro'); }
  };

  const handleChegada = async () => {
    if (!selected) return;
    await fetch(`${API}/api/viagem-acompanhamento/${selected.id}/chegada-destino`, { method: 'POST', headers });
    toast.success('Chegada registrada');
    const updated = await fetch(`${API}/api/viagem-acompanhamento/${selected.id}`, { headers });
    setSelected(await updated.json());
    fetchAll();
  };

  const handleSaida = async () => {
    if (!selected) return;
    await fetch(`${API}/api/viagem-acompanhamento/${selected.id}/saida-destino`, { method: 'POST', headers });
    toast.success('Saida registrada');
    const updated = await fetch(`${API}/api/viagem-acompanhamento/${selected.id}`, { headers });
    setSelected(await updated.json());
    fetchAll();
  };

  const handleVerRelatorio = async (acomp) => {
    const r = await fetch(`${API}/api/viagem-acompanhamento/${acomp.id}/relatorio`, { headers });
    setShowRelatorio(await r.json());
  };

  const handleDelete = async (acomp) => {
    if (!window.confirm(`Remover acompanhamento de ${acomp.empurrador_nome}?\nEsta acao nao pode ser desfeita.`)) return;
    const r = await fetch(`${API}/api/viagem-acompanhamento/${acomp.id}`, { method: 'DELETE', headers });
    if (r.ok) {
      toast.success('Acompanhamento removido');
      if (selected?.id === acomp.id) setSelected(null);
      fetchAll();
    } else { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-cyan-400 font-mono">Carregando...</div></div>;

  const emNav = acompanhamentos.filter(a => a.status === 'em_navegacao');
  const noDest = acompanhamentos.filter(a => a.status === 'no_destino');
  const concluidas = acompanhamentos.filter(a => a.status === 'concluida');
  const viagensSemAcomp = viagensEmAndamento.filter(v => !acompanhamentos.find(a => a.viagem_id === v.id));

  // Filtered list
  const filteredAcomp = acompanhamentos.filter(a => {
    if (filtroStatus !== 'todos' && a.status !== filtroStatus) return false;
    if (filtroRota && a.rota_id !== filtroRota) return false;
    if (filtroBusca && !a.empurrador_nome?.toLowerCase().includes(filtroBusca.toLowerCase()) && !a.numero_viagem?.toLowerCase().includes(filtroBusca.toLowerCase())) return false;
    return true;
  });

  const rotasUnicas = [...new Map(acompanhamentos.map(a => [a.rota_id, { id: a.rota_id, nome: a.rota_nome }])).values()];

  return (
    <div className="space-y-5" data-testid="acomp-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Navigation className="w-6 h-6 text-blue-400" />Acompanhamento de Viagens</h1>
          <p className="text-sm text-slate-500">Check-points de rota e previsao de chegada</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchAll} className="h-7 text-xs"><RefreshCw className="w-3 h-3 mr-1" />Atualizar</Button>
          {isAdmin && emNav.length > 0 && <Button size="sm" variant="outline" onClick={async () => { const r = await fetch(`${API}/api/viagem-acompanhamento/auto-checkpoint`, { method: 'POST', headers }); const d = await r.json(); toast.success(d.message); fetchAll(); }} className="h-7 text-xs border-emerald-600 text-emerald-400 hover:bg-emerald-600/10" data-testid="btn-auto-cp"><CheckCircle className="w-3 h-3 mr-1" />Auto Check-Point</Button>}
          {rotas.length === 0 && isAdmin && <Button size="sm" onClick={handleSeed} className="bg-cyan-600 h-7 text-xs">Criar Rotas</Button>}
          {isAdmin && viagensSemAcomp.length > 0 && <Button size="sm" onClick={() => setShowIniciar(true)} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid="btn-iniciar"><Plus className="w-3 h-3 mr-1" />Iniciar Acompanhamento</Button>}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Em Navegacao', value: emNav.length, color: 'text-blue-400', border: 'border-blue-500/20' },
          { label: 'No Destino', value: noDest.length, color: 'text-amber-400', border: 'border-amber-500/20' },
          { label: 'Concluidas', value: concluidas.length, color: 'text-emerald-400', border: 'border-emerald-500/20' },
          { label: 'Sem Acompanhamento', value: viagensSemAcomp.length, color: 'text-slate-400', border: 'border-slate-700' },
        ].map((c, i) => (
          <div key={i} className={`bg-slate-900/80 border ${c.border} rounded-lg p-3 text-center`}>
            <p className="text-[10px] text-slate-500 uppercase">{c.label}</p>
            <p className={`text-2xl font-bold font-mono ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List with Filters */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-400 uppercase">Viagens Acompanhadas</h3>
          {/* Filters */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-2.5 space-y-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)} placeholder="Buscar embarcacao..."
                className="w-full bg-slate-800 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-xs text-white" data-testid="filtro-busca" />
            </div>
            <div className="flex gap-1">
              {[
                { v: 'todos', l: 'Todos', c: 'bg-slate-700 text-slate-300' },
                { v: 'em_navegacao', l: 'Navegando', c: 'bg-blue-500/20 text-blue-400' },
                { v: 'no_destino', l: 'No Destino', c: 'bg-amber-500/20 text-amber-400' },
                { v: 'concluida', l: 'Concluida', c: 'bg-emerald-500/20 text-emerald-400' },
              ].map(f => (
                <button key={f.v} onClick={() => setFiltroStatus(f.v)} data-testid={`filtro-${f.v}`}
                  className={`px-2 py-1 rounded text-[10px] font-bold transition ${filtroStatus === f.v ? f.c + ' ring-1 ring-white/20' : 'bg-slate-800 text-slate-500 hover:text-slate-300'}`}>{f.l}</button>
              ))}
            </div>
            {rotasUnicas.length > 1 && (
              <select value={filtroRota} onChange={e => setFiltroRota(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white" data-testid="filtro-rota">
                <option value="">Todas as Rotas</option>
                {rotasUnicas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
            )}
            <p className="text-[10px] text-slate-500 text-right">{filteredAcomp.length} de {acompanhamentos.length}</p>
          </div>

          {filteredAcomp.length === 0 && <p className="text-sm text-slate-500 p-4 bg-slate-900/50 rounded">Nenhuma viagem encontrada</p>}
          {filteredAcomp.map(a => (
            <div key={a.id} className={`relative group rounded-lg border transition ${selected?.id === a.id ? 'bg-cyan-500/10 border-cyan-500/40' : 'bg-slate-900/60 border-slate-800 hover:border-slate-600'}`}>
              <button onClick={() => setSelected(a)} data-testid={`acomp-${a.id.slice(0,8)}`} className="w-full text-left p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-white">{a.empurrador_nome}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${a.status === 'em_navegacao' ? 'bg-blue-500/20 text-blue-400' : a.status === 'no_destino' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{a.status.replace('_', ' ')}</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{a.rota_nome}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{a.numero_viagem}</p>
                <div className="flex gap-1 mt-1.5">
                  {a.checkpoints?.map((cp, i) => (
                    <div key={i} className={`w-3 h-3 rounded-full border ${cp.cumprido ? 'bg-emerald-500 border-emerald-400' : 'bg-slate-700 border-slate-600'}`} title={cp.nome} />
                  ))}
                </div>
              </button>
              {isAdmin && (
                <button onClick={(e) => { e.stopPropagation(); handleDelete(a); }} title="Excluir acompanhamento"
                  className="absolute top-2 right-2 p-1 rounded bg-slate-800/80 opacity-0 group-hover:opacity-100 transition hover:bg-red-900/50" data-testid={`delete-${a.id.slice(0,8)}`}>
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-2">
          {selected ? <DetailPanel acomp={selected} onPassagem={handlePassagem} onChegada={handleChegada} onSaida={handleSaida} onRelatorio={handleVerRelatorio} isAdmin={isAdmin} /> : (
            <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-12 text-center">
              <Navigation className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">Selecione uma viagem para ver o acompanhamento</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Iniciar */}
      {showIniciar && <IniciarModal viagens={viagensSemAcomp} rotas={rotas} onIniciar={handleIniciar} onClose={() => setShowIniciar(false)} />}

      {/* Modal Relatorio */}
      {showRelatorio && <RelatorioModal rel={showRelatorio} onClose={() => setShowRelatorio(null)} />}
    </div>
  );
}

function DetailPanel({ acomp, onPassagem, onChegada, onSaida, onRelatorio, isAdmin }) {
  const [obsInput, setObsInput] = useState('');
  const [regCpId, setRegCpId] = useState(null);
  const cps = acomp.checkpoints || [];
  const cumpridos = cps.filter(c => c.cumprido).length;
  const total = cps.length;
  const progresso = total > 0 ? Math.round((cumpridos / (total + 1)) * 100) : 0;
  const allCpsDone = cumpridos === total;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg overflow-hidden" data-testid="detail-panel">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-white">{acomp.empurrador_nome} <span className="text-slate-500 font-normal text-sm">| {acomp.numero_viagem}</span></h2>
          <p className="text-xs text-slate-400">{acomp.rota_nome} | Cmd: {acomp.comandante}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => onRelatorio(acomp)} className="h-7 text-xs"><FileText className="w-3 h-3 mr-1" />Relatorio</Button>
      </div>

      {/* Route Visualization */}
      <div className="p-4">
        <div className="flex items-center gap-0 overflow-x-auto pb-2">
          {/* Origem */}
          <div className="flex flex-col items-center min-w-[60px]">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center"><Anchor className="w-4 h-4 text-emerald-400" /></div>
            <span className="text-[9px] text-emerald-400 mt-1 text-center leading-tight max-w-[70px]">{acomp.origem?.nome?.split(' ').slice(-2).join(' ')}</span>
          </div>
          {cps.map((cp, i) => {
            const isNext = !cp.cumprido && (i === 0 || cps[i - 1].cumprido);
            return (
              <React.Fragment key={i}>
                <div className={`flex-1 h-0.5 min-w-[30px] ${cp.cumprido ? 'bg-emerald-500' : isNext ? 'bg-blue-500 animate-pulse' : 'bg-slate-700'}`} />
                <div className="flex flex-col items-center min-w-[60px] relative">
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center ${cp.cumprido ? 'bg-emerald-500/20 border-emerald-500' : isNext ? 'bg-blue-500/20 border-blue-500 ring-2 ring-blue-500/30' : 'bg-slate-800 border-slate-600'}`}>
                    {cp.cumprido ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : isNext ? <Ship className="w-3.5 h-3.5 text-blue-400" /> : <Circle className="w-3 h-3 text-slate-500" />}
                  </div>
                  <span className={`text-[9px] mt-1 text-center leading-tight max-w-[70px] ${cp.cumprido ? 'text-emerald-400' : isNext ? 'text-blue-400' : 'text-slate-500'}`}>{cp.nome}</span>
                  {cp.cumprido && <span className="text-[8px] text-slate-500">{fmtDt(cp.hora_passagem).split(',')[1]}</span>}
                </div>
              </React.Fragment>
            );
          })}
          <div className={`flex-1 h-0.5 min-w-[30px] ${acomp.hora_chegada_destino ? 'bg-emerald-500' : allCpsDone ? 'bg-blue-500 animate-pulse' : 'bg-slate-700'}`} />
          <div className="flex flex-col items-center min-w-[60px]">
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${acomp.hora_chegada_destino ? 'bg-emerald-500/20 border-emerald-500' : 'bg-slate-800 border-slate-600'}`}>
              <Flag className={`w-4 h-4 ${acomp.hora_chegada_destino ? 'text-emerald-400' : 'text-slate-500'}`} />
            </div>
            <span className={`text-[9px] mt-1 text-center leading-tight max-w-[70px] ${acomp.hora_chegada_destino ? 'text-emerald-400' : 'text-slate-500'}`}>{acomp.destino?.nome?.split(' ').slice(-2).join(' ')}</span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 bg-slate-800 rounded-full h-1.5"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${acomp.hora_chegada_destino ? 100 : progresso}%` }} /></div>
          <span className="text-xs text-slate-400 font-mono">{cumpridos}/{total} CPs</span>
        </div>
      </div>

      {/* ETA */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-3">
        <div className="bg-slate-800/50 rounded p-2.5">
          <p className="text-[10px] text-slate-500 uppercase">Saida</p>
          <p className="text-xs text-white font-mono">{fmtDt(acomp.data_saida)}</p>
        </div>
        <div className="bg-slate-800/50 rounded p-2.5">
          <p className="text-[10px] text-slate-500 uppercase">ETA Previsto</p>
          <p className="text-xs text-amber-400 font-mono">{fmtDt(acomp.eta_destino_previsto)}</p>
        </div>
        <div className="bg-slate-800/50 rounded p-2.5">
          <p className="text-[10px] text-slate-500 uppercase">ETA Atualizado</p>
          <p className="text-xs text-cyan-400 font-mono font-bold">{fmtDt(acomp.eta_destino_atualizado)}</p>
        </div>
      </div>

      {/* Permanencia */}
      {acomp.hora_chegada_destino && (
        <div className="px-4 pb-3 grid grid-cols-3 gap-3">
          <div className="bg-emerald-900/20 rounded p-2.5 border border-emerald-800/30">
            <p className="text-[10px] text-emerald-500 uppercase">Chegada Destino</p>
            <p className="text-xs text-emerald-400 font-mono">{fmtDt(acomp.hora_chegada_destino)}</p>
          </div>
          <div className="bg-slate-800/50 rounded p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">Saida Destino</p>
            <p className="text-xs text-white font-mono">{acomp.hora_saida_destino ? fmtDt(acomp.hora_saida_destino) : 'Aguardando'}</p>
          </div>
          <div className="bg-slate-800/50 rounded p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">Permanencia</p>
            <p className="text-xs text-amber-400 font-mono font-bold">{acomp.tempo_permanencia_h ? fmtH(acomp.tempo_permanencia_h) : 'Em curso'}</p>
          </div>
        </div>
      )}

      {/* Checkpoints Table */}
      <div className="border-t border-slate-800">
        <div className="px-4 py-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-slate-400 uppercase">Check-Points</h3>
          {isAdmin && acomp.status === 'em_navegacao' && allCpsDone && !acomp.hora_chegada_destino && (
            <Button size="sm" onClick={onChegada} className="bg-emerald-600 hover:bg-emerald-500 h-6 text-[10px]" data-testid="btn-chegada"><Flag className="w-3 h-3 mr-1" />Registrar Chegada</Button>
          )}
          {isAdmin && acomp.status === 'no_destino' && !acomp.hora_saida_destino && (
            <Button size="sm" onClick={onSaida} className="bg-amber-600 hover:bg-amber-500 h-6 text-[10px]" data-testid="btn-saida"><ArrowRight className="w-3 h-3 mr-1" />Registrar Saida</Button>
          )}
        </div>
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-800/40 text-slate-500 uppercase">
            <th className="px-4 py-1.5 text-left">Check-Point</th>
            <th className="px-4 py-1.5 text-center">Dist. (NM)</th>
            <th className="px-4 py-1.5 text-center">ETA Previsto</th>
            <th className="px-4 py-1.5 text-center">ETA Atualizado</th>
            <th className="px-4 py-1.5 text-center">Hora Real</th>
            <th className="px-4 py-1.5 text-center">Status</th>
            {isAdmin && acomp.status === 'em_navegacao' && <th className="px-4 py-1.5 text-center w-28">Acao</th>}
          </tr></thead>
          <tbody>
            {cps.map((cp, i) => {
              const isNext = !cp.cumprido && (i === 0 || cps[i - 1].cumprido);
              return (
                <tr key={i} className={`border-t border-slate-800/50 ${isNext ? 'bg-blue-500/5' : ''}`}>
                  <td className="px-4 py-2 flex items-center gap-2">
                    {cp.cumprido ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Circle className="w-3.5 h-3.5 text-slate-500" />}
                    <span className={cp.cumprido ? 'text-emerald-400 font-medium' : isNext ? 'text-blue-400 font-medium' : 'text-slate-400'}>{cp.nome}</span>
                  </td>
                  <td className="px-4 py-2 text-center text-slate-400 font-mono">{cp.distancia_acumulada_nm}</td>
                  <td className="px-4 py-2 text-center text-slate-500 font-mono">{fmtDt(cp.eta_previsto)}</td>
                  <td className="px-4 py-2 text-center text-cyan-400 font-mono">{fmtDt(cp.eta_atualizado)}</td>
                  <td className="px-4 py-2 text-center font-mono">{cp.hora_passagem ? <span className="text-emerald-400">{fmtDt(cp.hora_passagem)}</span> : <span className="text-slate-600">-</span>}</td>
                  <td className="px-4 py-2 text-center">{cp.cumprido ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">cumprido</span> : isNext ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">proximo</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-500">pendente</span>}</td>
                  {isAdmin && acomp.status === 'em_navegacao' && (
                    <td className="px-4 py-2 text-center">
                      {isNext && !cp.cumprido ? (
                        regCpId === cp.checkpoint_id ? (
                          <div className="flex gap-1 items-center">
                            <input value={obsInput} onChange={e => setObsInput(e.target.value)} placeholder="Obs..." className="bg-slate-800 border border-slate-600 rounded px-1.5 py-0.5 text-[10px] text-white w-20" />
                            <button onClick={() => { onPassagem(cp.checkpoint_id, obsInput); setRegCpId(null); setObsInput(''); }} className="px-1.5 py-0.5 bg-emerald-600 text-white rounded text-[10px]">OK</button>
                            <button onClick={() => setRegCpId(null)} className="text-slate-500 text-[10px]">X</button>
                          </div>
                        ) : (
                          <button onClick={() => setRegCpId(cp.checkpoint_id)} className="px-2 py-0.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-[10px] transition" data-testid={`btn-registrar-${i}`}>
                            <Play className="w-3 h-3 inline mr-0.5" />Registrar
                          </button>
                        )
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IniciarModal({ viagens, rotas, onIniciar, onClose }) {
  const [viagemId, setViagemId] = useState('');
  const [rotaId, setRotaId] = useState('');
  const [vel, setVel] = useState(6);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="iniciar-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">Iniciar Acompanhamento</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div><label className="text-xs text-slate-400">Viagem em Andamento *</label>
            <select value={viagemId} onChange={e => setViagemId(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="select-viagem">
              <option value="">Selecione...</option>
              {viagens.map(v => <option key={v.id} value={v.id}>{v.numero_viagem} - {v.origem}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-400">Rota *</label>
            <select value={rotaId} onChange={e => setRotaId(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="select-rota">
              <option value="">Selecione...</option>
              {rotas.map(r => <option key={r.id} value={r.id}>{r.nome} ({r.checkpoints?.length} CPs)</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-400">Velocidade Media (nos)</label>
            <input type="number" value={vel} onChange={e => setVel(+e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button size="sm" className="bg-emerald-600" onClick={() => onIniciar(viagemId, rotaId, vel)} disabled={!viagemId || !rotaId} data-testid="btn-confirm-iniciar">Iniciar</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RelatorioModal({ rel, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="relatorio-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">Relatorio da Viagem - {rel.viagem}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><span className="text-slate-500">Rota:</span> <span className="text-white">{rel.rota}</span></div>
            <div><span className="text-slate-500">Embarcacao:</span> <span className="text-white">{rel.embarcacao}</span></div>
            <div><span className="text-slate-500">Comandante:</span> <span className="text-white">{rel.comandante}</span></div>
            <div><span className="text-slate-500">Saida:</span> <span className="text-white">{fmtDt(rel.data_saida)}</span></div>
            <div><span className="text-slate-500">ETA Previsto:</span> <span className="text-amber-400">{fmtDt(rel.eta_previsto)}</span></div>
            <div><span className="text-slate-500">ETA Atualizado:</span> <span className="text-cyan-400">{fmtDt(rel.eta_atualizado)}</span></div>
            {rel.hora_chegada && <div><span className="text-slate-500">Chegada:</span> <span className="text-emerald-400">{fmtDt(rel.hora_chegada)}</span></div>}
            {rel.tempo_total_viagem_h && <div><span className="text-slate-500">Tempo Total:</span> <span className="text-white font-bold">{fmtH(rel.tempo_total_viagem_h)}</span></div>}
            {rel.tempo_permanencia_h != null && <div><span className="text-slate-500">Permanencia:</span> <span className="text-amber-400">{fmtH(rel.tempo_permanencia_h)}</span></div>}
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-800/40 text-slate-500 uppercase">
              <th className="px-3 py-1.5 text-left">Check-Point</th>
              <th className="px-3 py-1.5 text-center">Previsto</th>
              <th className="px-3 py-1.5 text-center">Real</th>
              <th className="px-3 py-1.5 text-center">Diferenca</th>
              <th className="px-3 py-1.5 text-center">Status</th>
              <th className="px-3 py-1.5 text-left">Obs</th>
            </tr></thead>
            <tbody>
              {(rel.checkpoints || []).map((cp, i) => (
                <tr key={i} className="border-t border-slate-800/50">
                  <td className="px-3 py-1.5 text-white">{cp.nome}</td>
                  <td className="px-3 py-1.5 text-center text-slate-400 font-mono">{fmtDt(cp.hora_prevista)}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{cp.hora_real ? <span className="text-emerald-400">{fmtDt(cp.hora_real)}</span> : '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{cp.diferenca_min != null ? <span className={cp.diferenca_min > 0 ? 'text-red-400' : cp.diferenca_min < 0 ? 'text-emerald-400' : 'text-slate-400'}>{cp.diferenca_min > 0 ? '+' : ''}{cp.diferenca_min}min</span> : '-'}</td>
                  <td className="px-3 py-1.5 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded ${cp.status === 'atrasado' ? 'bg-red-500/15 text-red-400' : cp.status === 'antecipado' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>{cp.status}</span></td>
                  <td className="px-3 py-1.5 text-slate-400">{cp.observacoes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
